
import { Component, Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { Group, GroupUiClient, GroupUserExternal } from '../../backend/backendapi';
import { blobDownload, emptyUUID } from '../../../util';
import GroupStore from '../../../stores/GroupStore';
import ExternalUserParseModal from './ExternalUserParseModal';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { paginationOptions, searchOptions, selectOptions } from '../../../util/vue-good-table';
import ExternalUserEditModal from './ExternalUserEditModal';
import ExternalUserSelectModal from './ExternalUserSelectModal';
import { initClient } from '../../../api';
import { applyGroupSelector, GroupSelector } from '../util';

@Component({
  components: {
    ExternalUserEditModal,
    ExternalUserParseModal,
    ExternalUserSelectModal,
    VueGoodTable,
  },
})
export default class ExternalUsersEditor<T> extends Vue {
  @Prop({ required: true })
  readonly group!: T;
  @Prop({ default: '' })
  readonly groupSelector!: GroupSelector<T>;
  @Prop({ required: true })
  readonly availableSalutations!: string[];
  @Prop({ default: false })
  readonly hideDisabled!: boolean;
  @Prop({ default: false })
  readonly hideMassSelection!: boolean;
  @Prop({ default: false })
  readonly allowNoEmailForExternalUsers!: boolean;

  readonly store = new GroupStore();

  private readonly api = initClient(GroupUiClient);
  private groupUserExternalEditingInstance: GroupUserExternal | null = null; //only set in when editing an external user, NOT when adding
  selectedRows: GroupUserExternal[] = [];

  get columnsConfig(): any[] {
    const ret: any [] = [{
      label: this.$t('firstname').toString(),
      field: (e: GroupUserExternal) => e.firstName,
    }, {
      label: this.$t('lastname').toString(),
      field: (e: GroupUserExternal) => e.lastName,
    }, {
      label: this.$t('email').toString(),
      field: (e: GroupUserExternal) => e.email,
    }, {
      label: this.$t('company').toString(),
      field: (e: GroupUserExternal) => e.company,
    }, {
      label: this.$t('salutation').toString(),
      field: (e: GroupUserExternal) => e.salutation,
    }];
    if (!this.hideDisabled) {
      ret.push({
        label: this.$t('group.groupUserExternal.model.disabled').toString(),
        field: 'disabled',
      });
    }
    ret.push({
      label: this.$i18n.t('general.actions').toString(),
      field: 'actions',
      width: '5em',
    });
    return ret;
  }

  get extractedGroup(): Group {
    return applyGroupSelector(this.group, this.groupSelector);
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  get selectOptions(): any {
    return selectOptions(this.$i18n);
  }

  //events
  addUser(): void {
    let groupUserExternal = new GroupUserExternal({
      id: emptyUUID(),
      groupId: this.extractedGroup.id,
      addedOn: new Date(),
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      salutation: '',
      code: 0,
      disabled: false,
    });
    this.extractedGroup.externalUsers ??= [];
    (this.$refs.editModal as ExternalUserEditModal).open(groupUserExternal);
  }

  editUser(user: GroupUserExternal): void {
    let groupUserExternal = this.getRealExternalUserObject(user);
    this.groupUserExternalEditingInstance = groupUserExternal;
    (this.$refs.editModal as ExternalUserEditModal).open(groupUserExternal);
  }

  getRealExternalUserObject(user: GroupUserExternal): GroupUserExternal {
    this.extractedGroup.externalUsers ??= [];
    if (user.id?.length && user.id !== emptyUUID()) {
      return this.extractedGroup.externalUsers.find(e => e.id === user.id)!;
    } else {
      return this.extractedGroup.externalUsers.find(e => e.email === user.email
          && e.firstName === user.firstName
          && e.lastName === user.lastName
          && e.salutation === user.salutation)!;
    }
  }

  onSelectionChanged(params: { selectedRows: GroupUserExternal[] }): void {
    this.selectedRows = params.selectedRows.map(e => this.getRealExternalUserObject(e));
    console.log(this.selectedRows);
  }

  onUsersParsed(users: GroupUserExternal[]): void {
    (this.$refs.importModal as ExternalUserParseModal).dismiss();
    this.extractedGroup.externalUsers ??= [];
    this.extractedGroup.externalUsers.push(...users.map(e => GroupUserExternal.fromJS(e)));
  }

  onUsersSelected(users: GroupUserExternal[]): void {
    //filter out users already present in the group
    const presentEmails = new Set(this.extractedGroup.externalUsers.map(e => e.email.toLowerCase()));
    users = users.filter(e => !presentEmails.has(e.email.toLowerCase()));

    //
    (this.$refs.selectModal as ExternalUserSelectModal).dismiss();
    this.extractedGroup.externalUsers ??= [];
    this.extractedGroup.externalUsers.push(...users.map(e => GroupUserExternal.fromJS(e)));
  }

  onExternalUserSave(user: GroupUserExternal): void {
    (this.$refs.editModal as ExternalUserEditModal).dismiss();
    if (this.groupUserExternalEditingInstance) {
      Object.assign(this.groupUserExternalEditingInstance, user);
    } else {
      this.extractedGroup.externalUsers.push(user);
    }
  }

  openParseUsersModal(): void {
    (this.$refs.importModal as ExternalUserParseModal).open();
  }

  async onExportUsers(): Promise<void> {
    try {
      await this.api.listGroupUserExternal(this.extractedGroup.id)
          .then(response => {
            // Use the filename from response, or fallback to a default string
            const finalFileName = response.fileName ?? `users-instruction-${Date.now()}.xlsx`;
            blobDownload(response.data, finalFileName);
          });
    } catch (error) {
      console.error('Download failed:', error);
    }
  }

  openSelectModal(): void {
    (this.$refs.selectModal as ExternalUserSelectModal).open();
  }

  removeUser(user: GroupUserExternal): void {
    user = this.getRealExternalUserObject(user);
    if (!user) {
      return;
    }

    this.extractedGroup.externalUsers ??= [];
    const index = this.extractedGroup.externalUsers.findIndex(e => e === user);
    if (index >= 0) {
      this.extractedGroup.externalUsers.splice(index, 1);
    }
  }
}
